/**
 * A control chart's signals as episodes (S9b): a run of signals of one rule on one sub-chart is one event, not a
 * wall of red dots. The summary line counts them; each episode says which rule, when, over how many points,
 * whether it came from history (imported or backlog data, which never alarms) and how far its furthest point
 * sat from the center line. A sustained episode (the process still sits on one side of its center line)
 * offers writers a re-baseline from where that run began. An older server without episodes shows the count.
 *
 * SpcEpisodesView is pure (rendered in tests without providers); SpcEpisodes holds the org and the dialog.
 */
import { useState } from "react";
import { cn } from "@/lib/ui/cn";
import { useOrg } from "@/lib/org-context";
import { spcRuleLabel, type SpcEpisode, type SpcSeries } from "@/lib/api/spc";
import { spcWhen } from "@/lib/widgets/statistical/spc/plot";
import { groupEpisodes, rebaselineDraft, spcSignalSummary, standardDeviations, type RebaselineDraft } from "./episodes";
import { SpcRebaselineDialog } from "./SpcRebaselineDialog";

const ORIGIN: Record<SpcEpisode["origin"], { text: string; cls: string }> = {
  live: { text: "Live", cls: "bg-danger/10 text-danger" },
  historical: { text: "From past data, no alert is sent", cls: "bg-surface-muted text-on-surface-muted" },
  mixed: { text: "Live and history", cls: "bg-warning/15 text-warning" },
};

export interface SpcEpisodesViewProps {
  series: SpcSeries;
  /** Show only the newest `limit` episodes (a dashboard tile). */
  limit?: number;
  /** The viewer may change the chart's limits. */
  canWrite: boolean;
  onRebaseline?: (episode: SpcEpisode) => void;
  className?: string;
}

export function SpcEpisodesView({ series, limit, canWrite, onRebaseline, className }: SpcEpisodesViewProps) {
  const { groups, hidden } = groupEpisodes(series.episodes ?? [], series.chart.chart_type, limit);
  return (
    <div className={cn("flex flex-col gap-1.5 text-xs", className)}>
      <p className="font-medium text-on-surface">{spcSignalSummary(series)}</p>
      {groups.map((g) => (
        <section key={g.sub} aria-label={g.label} className="flex flex-col gap-1">
          <h4 className="text-[11px] font-semibold text-on-surface-muted">{g.label}</h4>
          <ul className="flex flex-col gap-1">
            {g.episodes.map((ep) => (
              <EpisodeItem
                key={`${ep.sub}-${ep.rule}-${ep.first_at}`}
                ep={ep}
                action={canWrite && ep.sustained ? onRebaseline : undefined}
              />
            ))}
          </ul>
        </section>
      ))}
      {hidden > 0 && (
        <p className="text-on-surface-muted">
          {hidden} older {hidden === 1 ? "episode" : "episodes"} not shown.
        </p>
      )}
    </div>
  );
}

function EpisodeItem({ ep, action }: { ep: SpcEpisode; action?: (episode: SpcEpisode) => void }) {
  const origin = ORIGIN[ep.origin] ?? ORIGIN.live;
  const when = ep.first_at === ep.last_at ? spcWhen(ep.first_at) : `${spcWhen(ep.first_at)} to ${spcWhen(ep.last_at)}`;
  const facts = [when, `${ep.points} ${ep.points === 1 ? "point" : "points"}`];
  if (ep.max_abs_z != null) facts.push(`furthest ${standardDeviations(ep.max_abs_z)} from the center line`);
  return (
    <li className="rounded-md border border-border/60 px-2 py-1.5">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="font-medium text-on-surface">{spcRuleLabel(ep.rule)}</span>
        <span className={cn("rounded px-1.5 py-px text-[10px] font-medium", origin.cls)}>{origin.text}</span>
      </div>
      <p className="text-on-surface-muted">{facts.join(" · ")}</p>
      {ep.sustained && (
        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5">
          <span className="text-on-surface">
            The process has sat on one side of its center line since {spcWhen(ep.run_from || ep.first_at)}.
          </span>
          {action && (
            <button type="button" className="font-medium text-brand hover:underline" onClick={() => action(ep)}>
              Re-baseline from here
            </button>
          )}
        </div>
      )}
    </li>
  );
}

/**
 * The episodes with the re-baseline dialog. The server lets a member, an admin or an owner change limits
 * (the twin routes' RequireWrite(member), where an owner ranks as an admin): canWrite covers the member and
 * the admin, canAdmin adds the owner.
 */
export function SpcEpisodes({ series, limit, className }: { series: SpcSeries; limit?: number; className?: string }) {
  const { canWrite, canAdmin } = useOrg();
  const [draft, setDraft] = useState<RebaselineDraft | null>(null);
  return (
    <>
      <SpcEpisodesView
        series={series}
        limit={limit}
        canWrite={canWrite || canAdmin}
        onRebaseline={(ep) => setDraft(rebaselineDraft(ep, Date.now()))}
        className={className}
      />
      {draft && <SpcRebaselineDialog chartId={series.chart.id} draft={draft} onClose={() => setDraft(null)} />}
    </>
  );
}
